import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BaseDrawer from "../../../components/ui/BaseDrawer";
import BaseTextField from "../../../components/ui/BaseTextField";
import {
  resetClientDashboardFilters,
  setClientDashboardFilters,
} from "../../../store/filters/clientDashboardFilterSlice";
import { fetchAllClients } from "../../../services/api/client";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { fetchCompanyBranches } from "../../../services/api/branch";
import { fetchTeams } from "../../../services/api/teams";

const ClientDashboardFilters = ({
  open,
  onClose,
  isLoading,
  setFiltersChanged,
  filterType = "client",
  module = "workOrders",
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.clientDashboardFilters[module]);
  const [clientSearchInput, setClientSearchInput] = useState("");

  const { auth } = useAuth();
  const companyId = auth?.user?.company?.id;

  const [localFilters, setLocalFilters] = useState({
    search: "",
    clientId: { value: "", label: "" },
    country: "",
    manager: "",
    branchId: { value: "", label: "" },
    teamIds: [],
    taxType: "",
    projectsCount: "",
    totalWorkOrdersCount: "",
    publishedCount: "",
    assignedCount: "",
    workDoneCount: "",
    approvedCount: "",
    paidCount: "",
  });

  const isParentFilter = filterType === "client";
  const clientListParam = isParentFilter ? "true" : "false";

  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", companyId, clientListParam],
    queryFn: () =>
      fetchAllClients({
        companyId: companyId?.toString(),
        isParentClient: clientListParam,
      }),
    enabled: !!companyId,
  });
  const {
    data: branchesData,
    isPending: isFetchCompanyBranchesApiLoading,
    error: isFetchCompanyBranchesApiError,
    refetch,
  } = useQuery({
    queryKey: ["branchesData"],
    queryFn: () => fetchCompanyBranches(),
  });
  const {
    data: teamsData,
    isLoading: isTeamsLoading,
    error: teamsError,
    refetch: refetchTeams,
  } = useQuery({
    queryKey: ["teamsData"],
    queryFn: () => fetchTeams(),
  });
  useEffect(() => {
    if (filters) {
      setLocalFilters({ ...filters });
    }
  }, [filters]);

  const handleApplyFilters = () => {
    Object.entries(localFilters).forEach(([key, value]) => {
      dispatch(
        setClientDashboardFilters({
          module,
          filterName: key,
          data: value,
        }),
      );
    });
    dispatch(
      setClientDashboardFilters({
        module,
        filterName: "pagination",
        data: { page: 0 },
      }),
    );

    setTimeout(() => {
      onClose();
    }, 0);
  };

  const handleResetFilters = () => {
    dispatch(resetClientDashboardFilters({ module }));
    setLocalFilters({
      search: "",
      clientId: { value: "", label: "" },
      country: "",
      manager: "",
      branchId: { value: "", label: "" },
      teamIds: [],
      taxType: "",
      projectsCount: "",
      totalWorkOrdersCount: "",
      publishedCount: "",
      assignedCount: "",
      workDoneCount: "",
      approvedCount: "",
      paidCount: "",
    });

    setFiltersChanged(true);
  };
  const formattedClientData =
    clientsData?.data?.data?.map((client) => ({
      label: client.clientName,
      value: client.id,
    })) || [];
  const formattedBranchData = branchesData?.data?.data?.map((branch) => ({
    label: branch.name,
    value: branch.id,
  }));
  const formattedTeamData =
    teamsData?.data?.data?.map((team) => ({
      label: team.name,
      value: team.id,
    })) || [];

  const hasFilters = Object.values(localFilters).some((v) => v && v.toString().trim() !== "");

  const handleClientChange = (event) => {
    setLocalFilters({
      ...localFilters,
      clientId: { value: event.target.value, label: event.target.label },
    });
  };
  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={handleApplyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isLoading}
      enabled={hasFilters}
      handleResetFilters={handleResetFilters}
    >
      <div>
        <div className="mb-7">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Customer"
            name="clientId"
            placeholder="Select Customer"
            options={formattedClientData}
            value={localFilters.clientId?.value || ""}
            onChange={handleClientChange}
            isClearable
            isDefaultField={false}
            onInputChange={(_, value) => setClientSearchInput(value)}
          />
        </div>
        {filterType !== "client" && (
          <div className="mb-7">
            <BaseAutoSelect
              label="Tech Domain Branch"
              name="branchId"
              placeholder="Branch Name"
              options={formattedBranchData}
              required={true}
              value={localFilters.branchId}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  branchId: { value: e.target.value, label: e.target.label },
                })
              }
              labelShrink={false}
              optional={false}
              isDefaultField={false}
              isClearable
            />
          </div>
        )}
        {filterType !== "client" && (
          <div className="mb-5">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Tech Domain Team"
              name="teamIds"
              multiple
              placeholder="Select Teams"
              options={formattedTeamData}
              value={localFilters.teamIds}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  teamIds: e.target.value,
                })
              }
              isClearable
            />
          </div>
        )}

        {filterType !== "client" && (
          <div className="mb-7">
            <BaseTextField
              label="Contact Person"
              name="manager"
              placeholder="Contact Person Name"
              value={localFilters.manager}
              onChange={(e) => setLocalFilters({ ...localFilters, manager: e.target.value })}
              isClearable
              labelShrink={false}
              optional={false}
              isDefaultField={false}
            />
          </div>
        )}
        <div className="mb-7">
          <BaseTextField
            label="Country"
            name="country"
            placeholder="Country"
            value={localFilters.country}
            onChange={(e) => setLocalFilters({ ...localFilters, country: e.target.value })}
            labelShrink={false}
            optional={false}
            isClearable
            isDefaultField={false}
          />
        </div>
        {filterType !== "client" && (
          <div className="mb-7">
            <BaseAutoSelect
              labelShrink={false}
              label="Tax Type"
              required={true}
              fullWidth
              options={[
                {
                  label: "GST",
                  value: "GST",
                },
                {
                  label: "VAT",
                  value: "VAT",
                },
              ]}
              value={localFilters.taxType}
              id="taxType"
              name="taxType"
              isDefaultField={false}
              onChange={(e) => setLocalFilters({ ...localFilters, taxType: e.target.value })}
            />
          </div>
        )}
      </div>
    </BaseDrawer>
  );
};

export default ClientDashboardFilters;
